import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { formatUSD, formatDate } from "../../lib/format";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import ReportPhoto from "../ReportPhoto";
import { CheckCircle, X, Wrench, Package, Users, Bed, CurrencyDollar, SealCheck, DownloadSimple, Timer, PiggyBank, TrendUp, TrendDown } from "@phosphor-icons/react";

const ROUTE_META = {
  interna:     { label: "irá a Mantenimiento", Ic: Wrench,   bg: "#1F3864", fg: "#fff" },
  repuesto:    { label: "irá a Compras",       Ic: Package,  bg: "#C9A227", fg: "#1a1200" },
  contratista: { label: "irá a Contratista",   Ic: Users,    bg: "#4b5563", fg: "#fff" },
};

function KPICard({ label, value, sub, Ic, tone, testid }) {
  const color = tone === "red" ? "#ef4444" : tone === "gold" ? "#C9A227" : "#1F3864";
  return (
    <div className="q-card p-4 flex items-center gap-3" data-testid={testid}>
      <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{background: color+"18", color}}><Ic size={20} weight="fill"/></div>
      <div className="flex-1">
        <div className="q-label text-[color:var(--q-muted)]">{label}</div>
        <div className="text-2xl font-mono font-light" style={{color}}>{value}</div>
        {sub && <div className="text-xs text-[color:var(--q-muted)]">{sub}</div>}
      </div>
    </div>
  );
}

export default function DashboardTab() {
  const { session } = useAuth();
  const nav = useNavigate();
  const [kpi, setKpi] = useState(null);
  const [reports, setReports] = useState([]);
  const isProtected = session?.hotel?.license_status === "PROTEGIDA";

  const load = useCallback(() => {
    api.get("/kpis").then(r => setKpi(r.data));
    api.get("/reports").then(r => setReports(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pending = reports.filter(r => r.status === "VALIDADO");
  const inFlight = reports.filter(r => ["EN_MANTENIMIENTO","ESPERANDO_COMPRA","ESPERANDO_REPUESTO","CONTRATISTA"].includes(r.status));
  const closed = reports.filter(r => r.status === "CERRADO").slice(0, 20);

  const approve = async (id) => {
    try { await api.post(`/reports/${id}/approve`); toast.success("Aprobado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const reject = async (id) => {
    try { await api.post(`/reports/${id}/reject`); toast.success("Rechazado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const exportJson = async (year, month) => {
    const url = year && month ? `/gerencia/export?year=${year}&month=${month}` : "/gerencia/export";
    const { data } = await api.get(url);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = `quilla-${data.hotel.license}-${data.range || "todo"}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(objUrl);
    setShowExport(false);
  };
  const [showExport, setShowExport] = useState(false);
  const [expYear, setExpYear] = useState(new Date().getFullYear());
  const [expMonth, setExpMonth] = useState(new Date().getMonth() + 1);

  if (!kpi) return <div className="q-card p-6">Cargando…</div>;
  const hi = kpi.health_index;
  const hiColor = hi.level === "bajo" ? "#10b981" : hi.level === "medio" ? "#C9A227" : "#ef4444";

  return (
    <div>
      <div className="grid md:grid-cols-12 gap-4 mb-6">
        <div className="md:col-span-8 q-card p-6 md:p-8">
          <div className="flex items-center justify-between mb-2">
            <div className="q-label text-[color:var(--q-muted)]">QUILLA Health Index · v1</div>
            <button onClick={() => setShowExport(true)} className="q-btn-ghost text-xs" data-testid="export-json">
              <DownloadSimple size={12} className="inline mr-1"/> Exportar JSON
            </button>
          </div>
          <div className="flex items-end gap-4">
            <div className="q-hero-num text-7xl md:text-8xl" data-testid="health-index-value" style={{color: hiColor}}>{hi.score}</div>
            <div className="pb-3">
              <div className="text-lg text-[color:var(--q-muted)] font-mono">/100</div>
              <div className="q-label mt-1" style={{color: hiColor}}>Riesgo {hi.level}</div>
            </div>
          </div>
          <div className="mt-6 q-healthbar">
            <div className="q-healthbar-marker" style={{left: hi.score + "%"}}/>
          </div>
          <div className="mt-4 text-xs">
            <span className="font-mono text-red-600 font-semibold">{hi.graves}</span> graves ·{" "}
            <span className="font-mono text-amber-600 font-semibold">{hi.medios}</span> medios ·{" "}
            <span className="font-mono text-blue-600 font-semibold">{hi.leves}</span> leves ·{" "}
            Habs. FS: <span className="font-mono font-semibold">{hi.rooms_out_of_service}</span> · Seguridad:{" "}
            <span className="font-mono font-semibold">{hi.security_open}</span>
          </div>
        </div>
        <div className="md:col-span-4 grid gap-4">
          <KPICard testid="kpi-oos" label="Habitaciones FS" value={kpi.rooms_out_of_service} Ic={Bed} tone="red"/>
          <KPICard testid="kpi-dollar" label="Riesgo/día" value={formatUSD(kpi.dollar_risk_per_day)} Ic={CurrencyDollar} tone="gold"/>
          <KPICard testid="kpi-approve" label="Por aprobar" value={formatUSD(kpi.pending_approval_amount)} sub={kpi.pending_approval_count + " pendientes"} Ic={SealCheck} tone="navy"/>
        </div>
      </div>

      {/* Métricas ejecutivas: MTTR + Costo evitado */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="q-card p-5" data-testid="kpi-mttr">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{background:"#1F386418", color:"#1F3864"}}>
              <Timer size={20} weight="fill"/>
            </div>
            <div className="flex-1">
              <div className="q-label text-[color:var(--q-muted)]">Tiempo promedio de resolución · 30d</div>
              <div className="text-3xl font-mono font-light text-[color:var(--q-navy)]">
                {kpi.mttr_days == null ? "—" : `${kpi.mttr_days} d`}
              </div>
              <div className="text-xs text-[color:var(--q-muted)] mt-1 flex items-center gap-1">
                {kpi.mttr_trend_pct == null ? (
                  <span>sin comparación previa aún</span>
                ) : kpi.mttr_trend_pct < 0 ? (
                  <span className="text-emerald-600 flex items-center gap-1"><TrendDown size={12} weight="fill"/> {Math.abs(kpi.mttr_trend_pct)}% vs. 30d anteriores (mejor)</span>
                ) : kpi.mttr_trend_pct > 0 ? (
                  <span className="text-red-600 flex items-center gap-1"><TrendUp size={12} weight="fill"/> {kpi.mttr_trend_pct}% vs. 30d anteriores</span>
                ) : (
                  <span>sin cambio</span>
                )}
                {" · "}{kpi.closed_last_30d} cerradas
              </div>
            </div>
          </div>
        </div>
        <div className="q-card p-5" data-testid="kpi-cost-avoided">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{background:"#C9A22718", color:"#C9A227"}}>
              <PiggyBank size={20} weight="fill"/>
            </div>
            <div className="flex-1">
              <div className="q-label text-[color:var(--q-muted)]">Costo evitado del mes</div>
              <div className="text-3xl font-mono font-light" style={{color:"#C9A227"}}>{formatUSD(kpi.cost_avoided_month_usd)}</div>
              <div className="text-xs text-[color:var(--q-muted)] mt-1">
                {kpi.closed_last_30d > 0
                  ? `Resolviste ${kpi.closed_last_30d} órdenes en promedio ${kpi.mttr_days}d cada una`
                  : "Aún sin cierres este mes"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="mb-8">
        <h2 className="text-xl mb-3">Aprobaciones ({pending.length})</h2>
        <div className="grid gap-3">
          {pending.length === 0 && <div className="q-card p-6 text-center text-[color:var(--q-muted)]">Sin pendientes.</div>}
          {pending.map(r => {
            const rm = ROUTE_META[r.route] || ROUTE_META.interna;
            const RmIc = rm.Ic;
            const borderColor = r.severity === "grave" ? "#ef4444" : r.severity === "media" ? "#f59e0b" : "#3b82f6";
            return (
              <div key={r.id} data-testid={"approval-" + r.room} onClick={() => nav(`/report/${r.id}`)}
                   className="q-card p-4 cursor-pointer" style={{borderLeft: "4px solid " + borderColor}}>
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-wrap">
                  <div className="text-2xl font-mono font-light break-words min-w-0 basis-full sm:basis-auto sm:min-w-[3rem] sm:max-w-[10rem]">{r.room}</div>
                  {r.photo_paths && r.photo_paths[0] && <ReportPhoto path={r.photo_paths[0]} className="w-16 h-16 shrink-0"/>}
                  <div className="flex-1 min-w-[180px] basis-full sm:basis-auto order-3 sm:order-2">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={"chip chip-" + r.severity + " shrink-0"}>{r.severity}</span>
                      <span className="badge-route shrink-0" style={{background: rm.bg, color: rm.fg}}>
                        <RmIc size={11} weight="fill" className="mr-1 inline"/> {rm.label}
                      </span>
                    </div>
                    <div className="text-sm break-words">{r.note}</div>
                    <div className="text-xs text-[color:var(--q-muted)] mt-1 break-words">
                      Costo estimado: <span className="font-mono font-semibold">{formatUSD(r.estimated_cost || 0)}</span>
                      {r.tech_note ? " · " + r.tech_note : ""}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0 ml-auto sm:ml-0 order-2 sm:order-3" onClick={(e) => e.stopPropagation()}>
                    <button data-testid={"reject-" + r.room} disabled={isProtected} onClick={() => reject(r.id)} className="q-btn-ghost disabled:opacity-40"><X size={16}/></button>
                    <button data-testid={"approve-" + r.room} disabled={isProtected} onClick={() => approve(r.id)} className="q-btn-primary flex items-center gap-2 disabled:opacity-40">
                      <CheckCircle size={16} weight="fill"/> Aprobar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-xl mb-3">Órdenes en ejecución ({inFlight.length})</h2>
        <div className="grid gap-2">
          {inFlight.map(r => (
            <div key={r.id} onClick={() => nav(`/report/${r.id}`)} className="q-card p-3 flex items-center gap-3 text-sm cursor-pointer">
              <span className="font-mono w-12 text-[color:var(--q-navy)]">{r.room}</span>
              <span className={"chip chip-" + r.severity}>{r.severity}</span>
              <span className="flex-1 truncate">{r.note}</span>
              <span className="q-label text-[color:var(--q-muted)]">{r.status.replace(/_/g, " ")}</span>
            </div>
          ))}
          {inFlight.length === 0 && <div className="text-sm text-[color:var(--q-muted)] p-4">Sin órdenes activas.</div>}
        </div>
      </section>

      <section>
        <h2 className="text-xl mb-3">Bitácora de cerradas ({closed.length})</h2>
        <div className="grid gap-2">
          {closed.map(r => (
            <div key={r.id} onClick={() => nav(`/report/${r.id}`)} className="q-card p-3 flex items-center gap-3 text-sm opacity-90 cursor-pointer">
              <CheckCircle size={16} weight="fill" className="text-emerald-600"/>
              <span className="font-mono w-12">{r.room}</span>
              <span className="flex-1 truncate">{r.note}</span>
              <span className="q-label text-[color:var(--q-muted)]">{formatDate(r.closed_at || r.created_at)}</span>
            </div>
          ))}
        </div>
      </section>

      {showExport && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowExport(false)} data-testid="export-modal">
          <div className="q-card w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Exportar JSON</h3>
            <p className="text-xs text-[color:var(--q-muted)] mb-4">Descarga toda la información operativa del hotel. Puedes limitar los reportes a un mes específico.</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="q-label block mb-1">Año</label>
                <input type="number" value={expYear} onChange={(e) => setExpYear(parseInt(e.target.value) || expYear)}
                       data-testid="export-year"
                       className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
              </div>
              <div>
                <label className="q-label block mb-1">Mes</label>
                <select value={expMonth} onChange={(e) => setExpMonth(parseInt(e.target.value))}
                        data-testid="export-month"
                        className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)]">
                  {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"].map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button className="q-btn-ghost" onClick={() => setShowExport(false)}>Cancelar</button>
              <button className="q-btn-ghost" data-testid="export-all-btn" onClick={() => exportJson()}>Todo el histórico</button>
              <button className="q-btn-primary" data-testid="export-month-btn" onClick={() => exportJson(expYear, expMonth)}>Exportar mes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
